import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";
import PillarCTAWithPhoto from "@/components/PillarCTAWithPhoto";

export const faqItems = [
  {
    question: "Como comer bem trabalhando em turnos rotativos?",
    answer: "O segredo é planejar as refeições de acordo com os horários do turno, não com o relógio convencional. No acompanhamento, montamos um plano flexível que se adapta aos seus diferentes turnos."
  },
  {
    question: "Posso comer a comida do restaurante onde trabalho?",
    answer: "Depende do que está disponível. Muitos restaurantes oferecem staff meals, e podemos trabalhar juntos para você fazer as melhores escolhas dentro do que é servido. Se não houver uma boa opção, o meal prep é o caminho."
  },
  {
    question: "Não tenho energia para cozinhar depois do turno. O que faço?",
    answer: "Meal prep é a solução. Separar um dia para preparar tudo e ter refeições prontas durante a semana elimina a necessidade de cozinhar quando você está exausta. No acompanhamento, montamos um plano de meal prep que funcione para você."
  },
  {
    question: "Trabalhar em hospitality engorda?",
    answer: "Não necessariamente, mas a combinação de horários irregulares, estresse, cansaço e acesso fácil a comida pode facilitar o ganho de peso. Com planejamento e as escolhas certas, é possível manter uma alimentação equilibrada mesmo nessa rotina."
  },
  {
    question: "Como lidar com a fome no meio do turno?",
    answer: "Ter lanches práticos sempre com você é essencial. Castanhas, frutas, barrinhas de proteína ou sanduíches simples são opções que cabem no bolso e no intervalo curto."
  },
  {
    question: "O acompanhamento funciona para quem não tem horário fixo?",
    answer: "Sim, e é justamente para isso que ele serve. Meus planos são flexíveis e se adaptam à sua escala. Não importa se você trabalha de manhã, à noite ou em turnos rotativos."
  },
  {
    question: "Quanto custa o acompanhamento nutricional?",
    answer: "A consulta inicial dura cerca de 30 minutos e não tem custo. Nela conversamos sobre seus objetivos e sua escala de trabalho, e eu apresento as opções de acompanhamento e os valores. Cada plano é personalizado, e o valor depende do tipo de acompanhamento."
  },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button size="lg" variant="gradient" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" style={{ background: 'var(--gradient-green)' }} onClick={openDialog}>
      <Calendar className="mr-2 h-5 w-5" />Agendar consulta
    </Button>
    <Button size="lg" variant="gradient-outline" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}>
      <MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />
        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Clara Camille · Nutricionista</p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">Alimentação saudável trabalhando em hospitality na Austrália</h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">Turnos irregulares, cansaço e refeições no trabalho não precisam ser sinônimo de alimentação ruim. Aqui você encontra um guia prático para quem trabalha em hospitality.</p>
              <CTAButtons openDialog={openDialog} />
            </div>
          </section>

          {/* CONTEÚDO */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="prose prose-lg max-w-none space-y-12">

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Os desafios de comer bem em hospitality</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Trabalhar em hospitality na Austrália (restaurantes, cafés, hotéis, bares) traz desafios específicos para a alimentação:</p>
                  <ul className="space-y-3">
                    {[
                      "Turnos rotativos que mudam os horários das refeições toda semana",
                      "Intervalos curtos que dificultam comer com calma",
                      "Acesso fácil a comida do restaurante (nem sempre saudável)",
                      "Cansaço físico que tira a vontade de cozinhar em casa",
                      "Trabalho noturno que desregula o sono e a fome",
                      "Estresse do ambiente de trabalho que pode levar a comer por impulso",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Como planejar refeições com horários rotativos</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">O segredo não é seguir os horários tradicionais de café, almoço e jantar, e sim adaptar as refeições ao seu turno. O que importa é: comer antes de trabalhar, ter algo prático durante o turno e fazer uma refeição completa quando chegar em casa.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">No acompanhamento, eu monto um plano que se adapta à sua escala semanal. Se você trabalha de manhã, o plano é um. Se trabalha à noite, ajustamos. A flexibilidade é o diferencial.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">O que comer antes, durante e depois do turno</h2>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm sm:text-base">
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Momento</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Turno diurno</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Turno noturno</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Antes do turno</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Café da manhã completo: ovos, pão integral, fruta</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Jantar leve: sopa, wrap ou salada com proteína</td>
                        </tr>
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Durante o turno</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Lanche rápido: castanhas, fruta, barra de proteína</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Lanche rápido: sanduíche, iogurte, mix de nuts</td>
                        </tr>
                        <tr>
                          <td className="p-3 sm:p-4 text-foreground font-sans font-medium">Depois do turno</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Almoço ou jantar completo com proteína, carbs e vegetais</td>
                          <td className="p-3 sm:p-4 text-muted-foreground font-sans">Refeição leve: omelete, sopa ou iogurte com granola</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <PillarCTAWithPhoto
                  title="Sua escala muda toda semana?"
                  message="Eu monto um plano flexível que se adapta aos seus turnos, seja de manhã, à tarde ou à noite. Na consulta inicial, olhamos juntos a sua escala e eu explico como o plano se encaixa nela."
                  openDialog={openDialog}
                />

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Meal prep para quem trabalha em hospitality</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">O meal prep é a melhor ferramenta para quem tem horários irregulares. Com 2 a 3 horas no seu dia de folga, você prepara as refeições da semana inteira. Algumas dicas:</p>
                  <ul className="space-y-3">
                    {[
                      "Cozinhe arroz, feijão e uma proteína (frango, carne moída) em quantidade",
                      "Pique vegetais e deixe prontos em potes na geladeira",
                      "Monte marmitas divididas por refeição e congele as que não vai usar nos próximos 2 dias",
                      "Tenha sempre lanches prontos: castanhas em saquinhos, frutas lavadas, barrinhas",
                      "Use potes térmicos para levar comida quente para o trabalho",
                      "Invista em containers de qualidade que não vazem na bolsa",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Hidratação durante o turno</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Trabalhar em hospitality é fisicamente exigente, especialmente na cozinha. Manter uma garrafa de água sempre por perto e beber ao longo do turno é fundamental. Desidratação causa cansaço, dor de cabeça e até mais fome.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Sono e alimentação: a conexão que poucos conhecem</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Quem trabalha em turnos noturnos sabe o quanto o sono é afetado. E o sono desregulado impacta diretamente a alimentação: aumenta a fome, dificulta o controle do apetite e favorece escolhas menos saudáveis. No acompanhamento, trabalhamos estratégias de alimentação que ajudam a regular o sono e a energia.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Leia também no blog</h2>
                  <ul className="space-y-3">
                    <li><Link to="/blog/$slug" params={{ slug: "como-organizar-alimentacao-na-australia-com-rotina-corrida" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Como organizar a alimentação na Austrália com rotina corrida</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "como-montar-marmitas-na-australia-sem-passar-horas-cozinhando" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Como montar marmitas na Austrália sem passar horas cozinhando</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "guia-pratico-habitos-sono-fome-energia-para-imigrantes" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Guia prático de hábitos: sono, fome e energia para imigrantes</Link></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Tire suas dúvidas</p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">Perguntas frequentes</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="rounded-xl border border-border/50 bg-card px-5 sm:px-6">
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">{item.question}</AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div className="absolute inset-0 blur-sm scale-105" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16" style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)', border: '1px solid rgba(255, 255, 255, 0.25)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                <img
                  src={claraPortrait}
                  alt="Clara Camille"
                  className="w-20 h-20 rounded-full object-cover object-top border-2 border-white/50 shadow-lg mx-auto"
                  loading="lazy"
                />
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">Cansada de comer mal por causa do trabalho?</h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">Na consulta inicial, entendo sua escala e seus objetivos e explico como o acompanhamento se adapta à rotina de hospitality.</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto" onClick={openDialog}><Calendar className="mr-2 h-5 w-5" />Agendar consulta</Button>
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto" style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}><MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp</Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/guia-alimentacao-brasileiros-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Guia de alimentação</Link>
                <span className="text-border">·</span>
                <Link to="/como-emagrecer-morando-na-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Emagrecer na Austrália</Link>
                <span className="text-border">·</span>
                <Link to="/dieta-estudantes-brasileiros-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Estudantes</Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const AlimentacaoSaudavelHospitality = () => (
  <ScheduleProvider><PageContent /></ScheduleProvider>
);

export default AlimentacaoSaudavelHospitality;
